/* Core & libraries */
import React, { useState } from "react";
import { useTheme } from "react-jss";
import { FormattedMessage } from "react-intl";
import moment from "helpers/moment";
import { Link, useNavigate } from 'react-router-dom';

/* Helpers, constants, custom methods, internal libraries */
import { useHasPermission } from "../../../hooks/useHasPermission.js";
import { colors } from "../../../constants/colors.js";
import { EDIT_POLICY_ROUTE } from "../../../constants/routes.js";
import { useLocaleContext } from "../../../contexts/localeContext.js";
import { useNotificationContext } from "../../../contexts/notificationContext";

/* Components */
import UCGrid from "../../../components/ucgrid/index.js";
import UCButton from "../../../components/buttons/index.js";
import { ReactComponent as AssignIcon } from "../../../resources/images/aa-users-plus.svg"
import { ReactComponent as PlusSign } from '../../../resources/images/plus.svg';
import { ReactComponent as RemovePolicyIcon } from '../../../resources/images/aa-remove-policy.svg';
import PolicyNoData from "../../../components/policyNoPolicies/index.js"
import RemoveModal from "../../../components/modals/deleteActionModal"

/* Styles */
import styles from "./styles.js"

const PolicyGrid = (props) => {
  const {gridOptions, setGridOptions, policyResponse, displayShowAssignModal, deletePolicy} = props;
  const theme = useTheme();
  const classes = styles({ theme });
  const navigate = useNavigate();

  const globalLocale = useLocaleContext();
  const { checkPermission } = useHasPermission();
  const { setNotification } = useNotificationContext();

  const [selectedRow, setSelectedRow] = useState(null);
  const [showRemovePolicyModal, setShowRemovePolicyModal] = useState(false);

  const actionCallback = (actionType, row) => {
    switch (actionType) {
      case "delete":
        setShowRemovePolicyModal(true)
        break;
      case "edit":
        navigate(
          "../" + EDIT_POLICY_ROUTE.replace(":policyId", row.policyId),
          {
            state: {
              policyId: row.policyId,
              showSaveBtn: false,
            },
          }
        );
        break;
    }
  }

  let customProps = {};
  if (checkPermission("F_POLICY_MANAGEPOLICIES")) {
    customProps.actionsComponent = (row) => (
      <UCButton
        type="default"
        className={classes.assignBtn}
        text={<FormattedMessage id="policyGrid.assignButton"/>}
        icon={<AssignIcon />}
        onClick={() => displayShowAssignModal(row)}
        loadingStateStyling={classes.assingBtnLoadingState}
        spinner={{
          name: `assignTeamsPolicyButton${row.policyId}`,
          size: 20,
          color: colors.darkerGrey
        }}
      />
    )
  }

  const onRowClick = row => {}

  const deleteAction = async () => {
    const response = await deletePolicy(selectedRow?.policyId)
    if (response) {
      setNotification(
        "success",
        <FormattedMessage id="notification.deletePolicySuccessTitle" />,
        <FormattedMessage id="notification.deletePolicySuccessText" values={{name: selectedRow?.policyName}}/>
      )
      setShowRemovePolicyModal(false)
    }
  }

  return (
    <div className={classes.policyGrid}>
      <UCGrid
        gridName="policyGrid"
        data={ policyResponse?.results }
        columns={ policyResponse?.columns }
        actions={ policyResponse?.actions }
        actionsWidth={"60px"}
        handlePageChange={(page) => {setGridOptions({
          ...gridOptions,
          page
        })
        page > 1 ? navigate(`/app/policy?page=${page}`) : navigate(`/app/policy`)
        }}
        setSelectedRow={setSelectedRow}
        actionCallback={actionCallback}
        spinner="policyGridSpinner"
        customColumns={{
          lastUpdated: (row) => (
            <div className={classes.link}>{ moment(row.lastUpdated).locale(globalLocale).format("D MMM YYYY") }</div>
          ),
          assignedName: (row) => (
            <div className={classes.assignedName}>
            { parseInt(row.assignedCount) > 0 ?
              <>
                <span>{row.assignedName}</span>
                {parseInt(row.assignedCount) > 1 && 
                  <div>
                    <PlusSign />
                    <p>{parseInt(row.assignedCount) - 1}</p>
                  </div>
                }
              </>
              : <span className={classes.unassigned}><FormattedMessage id="policyGrid.unassigned"/></span>
            }
            </div>
          ),
          policyName: (row) => (
            <div className={`${classes.nameText} ${classes.link}`}>
              {row?.policyName}
            </div>
          )
        }}
        columnCustomWidth={{
          assignedName: "317px !important",
          policyName: "282px !important"
        }}
        columnCustomProps={[
          // {
          //   columnName: "actions",
          //   allowOverflow: true,
          //   style: {
          //     display: 'flex',
          //     justifyContent: 'end',
          //     marginRight: "10px"
          //   }
          // }
        ]}
        {...customProps}
        noDataComponent={ <PolicyNoData/> }
        onRowClick={onRowClick}
        // onRowClickRoute={row => EDIT_POLICY_ROUTE.replace(":policyId", row.policyId)}
      />
      {
        policyResponse?.actions && policyResponse.actions.map((action, i) => {
          switch (action) {
            case "delete":
              return <React.Fragment key={`${action}-${i}`}>
                <RemoveModal
                  showModal={showRemovePolicyModal}
                  setShowModal={setShowRemovePolicyModal}
                  setSelectedRow={setSelectedRow}
                  modalIcon={ <RemovePolicyIcon /> }
                  modalTitle={<FormattedMessage id="policyGrid.deleteModalTitle" values={{policyName: selectedRow?.policyName}}/>}
                  modalDescription={<FormattedMessage id="policyGrid.removePolicyDescrition" />}
                  submitCallback={deleteAction}
                  submitText={<FormattedMessage id="policyGrid.deletePolicy"/>}
                />
              </React.Fragment>
          }
        })
      }
    </div>
  )
}

export default PolicyGrid;